import { ERROR_MESSAGE } from "@constants/index";

const STORAGE_KEY = "csc_session_bootstrap_error";
/** When set, Login must not auto-redirect to Keycloak so the user can read the error first. */
const BLOCK_AUTO_SSO_KEY = "csc_block_auto_keycloak_login";
/** Avoid duplicate toast for the same bootstrap error (e.g. React Strict Mode double mount). */
const TOAST_ONCE_KEY = "csc_bootstrap_error_toast_done";

/** True when the user refreshed the document (F5 / reload). Used to reset login error UX once. */
export function isPageReloadNavigation() {
  if (typeof performance === "undefined") {
    return false;
  }
  const nav = performance.getEntriesByType("navigation")[0];
  return nav instanceof PerformanceNavigationTiming && nav.type === "reload";
}

/**
 * RFC 6750 Bearer challenges often put the reason only in WWW-Authenticate (no JSON body).
 */
function extractWwwAuthenticateErrorDescription(headers) {
  if (!headers || typeof headers !== "object") {
    return "";
  }
  const raw = headers["www-authenticate"] ?? headers["WWW-Authenticate"];
  if (typeof raw !== "string") {
    return "";
  }
  const m = /error_description\s*=\s*"((?:[^"\\]|\\.)*)"/i.exec(raw);
  if (m) {
    return m[1].replace(/\\(.)/g, "$1");
  }
  return "";
}

/**
 * Pulls a human-readable message from an axios/API error (auth/session 400 string body, or OAuth2 401 JSON / header).
 */
export function extractBackendErrorString(error) {
  // CORS / offline: no response; avoid storing the vague axios string as the "backend" message.
  if (!error?.response && (error?.code === "ERR_NETWORK" || error?.message === "Network Error")) {
    return "";
  }
  const data = error?.response?.data;
  if (typeof data === "string" && data.trim()) {
    return data.trim();
  }
  if (data && typeof data === "object") {
    if (typeof data.message === "string" && data.message.trim()) {
      return data.message.trim();
    }
    if (typeof data.error_description === "string" && data.error_description.trim()) {
      return data.error_description.trim();
    }
  }
  const fromWww = extractWwwAuthenticateErrorDescription(error?.response?.headers);
  if (fromWww) {
    return fromWww;
  }
  if (error?.message && String(error.message).trim()) {
    return String(error.message).trim();
  }
  return "";
}

export function setSessionBootstrapError(error) {
  const msg = extractBackendErrorString(error);
  try {
    sessionStorage.setItem(STORAGE_KEY, msg.length ? msg : "__generic__");
    sessionStorage.setItem(BLOCK_AUTO_SSO_KEY, "1");
  } catch {
    // ignore
  }
}

/**
 * Read pending bootstrap error without removing it (safe across redirect / remount).
 * @returns {string|null} message, empty string for generic, or null if none
 */
export function peekSessionBootstrapError() {
  try {
    const v = sessionStorage.getItem(STORAGE_KEY);
    if (v == null) {
      return null;
    }
    return v === "__generic__" ? "" : v;
  } catch {
    return null;
  }
}

/** Clears stored error and allows Login to auto-redirect to Keycloak again. */
export function clearPendingLoginErrorAndUnblockSso() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(BLOCK_AUTO_SSO_KEY);
    sessionStorage.removeItem(TOAST_ONCE_KEY);
  } catch {
    // ignore
  }
}

/** @returns {boolean} true the first time for this error flow; false if toast already fired (e.g. Strict Mode remount). */
export function consumeBootstrapErrorToastSlot() {
  try {
    if (sessionStorage.getItem(TOAST_ONCE_KEY) === "1") {
      return false;
    }
    sessionStorage.setItem(TOAST_ONCE_KEY, "1");
    return true;
  } catch {
    return true;
  }
}

/**
 * @returns {string|null} stored backend message, empty string for generic, or null if nothing stored
 */
export function consumeSessionBootstrapError() {
  const v = peekSessionBootstrapError();
  if (v !== null) {
    clearPendingLoginErrorAndUnblockSso();
  }
  return v;
}

/**
 * Maps backend/Keycloak-adjacent messages to i18n keys (legacy login errors + session bootstrap).
 * @returns {{ titleKey: string, messageKey: string } | { titleKey: string, literalMessage: string }}
 */
export function mapBackendLoginMessageToToast(backendMessage) {
  const generic = {
    titleKey: "toast.login.error.title",
    messageKey: "toast.login.error.message",
  };
  if (!backendMessage?.trim()) {
    return generic;
  }
  const m = backendMessage.trim();

  if (m === ERROR_MESSAGE.PASSWORD_NOT_CORRECT) {
    return { titleKey: "toast.login.password-not-correct.title", messageKey: "toast.login.password-not-correct.message" };
  }
  if (
    m === ERROR_MESSAGE.USER_NOT_FOUND ||
    m === "User not found" ||
    m === "User not found for Keycloak identity"
  ) {
    return { titleKey: "toast.login.user-not-found.title", messageKey: "toast.login.user-not-found.message" };
  }
  if (m === ERROR_MESSAGE.USER_HAD_EXPIRED || m === "User had expired!") {
    return { titleKey: "toast.login.user-had-expired.title", messageKey: "toast.login.user-had-expired.message" };
  }
  if (m === ERROR_MESSAGE.USER_HAD_DISABLED || m === "User had disabled!") {
    return { titleKey: "toast.login.user-had-disabled.title", messageKey: "toast.login.user-had-disabled.message" };
  }
  if (m === ERROR_MESSAGE.THE_ID_OR_PASSWORD_IS_INCORRECT) {
    return { titleKey: "toast.login.error.title", messageKey: "toast.login.error.message-incorrect" };
  }
  if (m === "User is linked to a different Keycloak identity") {
    return { titleKey: "toast.login.keycloak-identity-mismatch.title", messageKey: "toast.login.keycloak-identity-mismatch.message" };
  }
  if (m === "Keycloak identity already linked to another account") {
    return { titleKey: "toast.login.keycloak-identity-already-linked.title", messageKey: "toast.login.keycloak-identity-already-linked.message" };
  }
  if (m === "User not linked to CSC account" || m.includes("not linked to CSC")) {
    return { titleKey: "toast.login.account-not-linked.title", messageKey: "toast.login.account-not-linked.message" };
  }
  if (m.includes("License has expired") || m.includes("License has expired or invalid")) {
    return { titleKey: "toast.login.license-invalid.title", messageKey: "toast.login.license-invalid.message" };
  }

  return { titleKey: "toast.login.error.title", literalMessage: m };
}
